/** Merge requests: every pull request the job opened, and the gate it was judged by.
 *
 * This screen is the seam between the service and the track-data repository.
 * The service never merges anything itself — it collects uploads, and a job
 * running in GitHub Actions compiles them, opens one pull request per circuit
 * and asks the gate whether that pull request may merge itself. So what is
 * shown here is a report of somebody else's decision, and the table is built
 * to read as one: every criterion's value is printed whether it passed or
 * failed, because "crossings: 2 · —" explains an awaiting-review row and a
 * bare red cross does not.
 *
 * The gate is read as a list rather than as named fields. Which criteria were
 * judged depends on whether a circuit was being published for the first time
 * or updated (spec §6), and the job may add one; the four columns the design
 * gives are the four that are almost always there, and anything else the job
 * judged is carried in the status cell's tooltip rather than dropped.
 *
 * The three cards under the table restate the gate in prose. They are filled
 * from the thresholds the listing sends back, not from constants written here,
 * so an admin who changes the quorum on the Settings screen sees this page's
 * description of the policy change with it.
 *
 * "Run job now" is a dispatch, not a start: the run happens in Actions, and a
 * deployment with no dispatch token can only record that somebody asked. The
 * service says which of those two happened and this screen repeats it
 * verbatim, because a button that claims to have run a job that did not run
 * is the one failure mode worth designing against.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { AdminHeader } from '@/components/admin/AdminHeader';
import { CandidateMap } from '@/components/admin/CandidateMap';
import { invalidateAdminPulse } from '@/components/admin/adminPulse';
import {
  Card,
  EmptyState,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
  mergeRequestLabel,
  mergeRequestTone,
} from '@/components/ui';
import {
  criterionFor,
  extraCriteria,
  gateColour,
  gateValue,
} from '@/components/admin/gate';
import { adminMergeRequests, getMeta, runJob } from '@/lib/api';
import { DASH, count, metres, relativeTime } from '@/lib/format';
import type { TagTone } from '@/components/ui/Tag';
import type { GateRules, MergeRequest, MergeRequestList, ServiceMeta } from '@/lib/types';

// ── the run notice ────────────────────────────────────────────────────────

interface Notice {
  tone: 'ok' | 'warn';
  text: string;
}

export default function AdminMerge() {
  const [page, setPage] = useState<MergeRequestList | null>(null);
  const [meta, setMeta] = useState<ServiceMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [runError, setRunError] = useState<unknown>(null);
  const [oneOpen, setOneOpen] = useState(false);
  /** The row whose pull request is drawn under the table, by merge request id. */
  const [viewing, setViewing] = useState<string | null>(null);
  const [officialId, setOfficialId] = useState('');

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reply = await adminMergeRequests({ limit: 100 });
      if (!alive.current) return;
      setPage(reply);
      setError(null);
    } catch (thrown) {
      if (!alive.current) return;
      setError(thrown);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The repository's name and URL belong to the deployment, not to this
  // build: a self-hosted service files against its own fork. A failure here
  // costs the sentence its link and nothing else.
  useEffect(() => {
    void getMeta()
      .then((value) => {
        if (alive.current) setMeta(value);
      })
      .catch(() => undefined);
  }, []);

  const dispatch = useCallback(
    async (scope?: string) => {
      setRunning(true);
      setRunError(null);
      setNotice(null);
      try {
        const started = await runJob(scope ? { official_id: scope } : {});
        if (!alive.current) return;
        setNotice({
          tone: started.dispatched ? 'ok' : 'warn',
          text: started.reason,
        });
        setOneOpen(false);
        setOfficialId('');
        // The run changes exactly what the header reports, and the listing
        // it produces is what this table is for.
        invalidateAdminPulse();
        await load();
      } catch (thrown) {
        if (alive.current) setRunError(thrown);
      } finally {
        if (alive.current) setRunning(false);
      }
    },
    [load],
  );

  const rules: GateRules | null = page?.gate_rules ?? null;
  const rows: MergeRequest[] = page?.items ?? [];
  // Read from the rows each time, so a refresh that moved the pull request on
  // takes its map away with it.
  const viewed = rows.find((row) => row.id === viewing && isWaiting(row)) ?? null;
  const waiting = rows.filter((row) => isWaiting(row));
  const settled = rows.filter((row) => !isWaiting(row));

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section
        aria-label="Merge requests"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
            Every pull request the job opened against{' '}
            {meta?.track_data_url ? (
              <a href={meta.track_data_url} target="_blank" rel="noreferrer noopener">
                {meta.track_data_repo || 'the track-data repository'}
              </a>
            ) : (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                {meta?.track_data_repo || 'the track-data repository'}
              </span>
            )}
            . Gate values are printed either way.
          </span>

          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: 12 }}
              disabled={running}
              onClick={() => void dispatch()}
            >
              {running ? 'Running…' : 'Run job · all tracks'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12 }}
              aria-expanded={oneOpen}
              disabled={running}
              onClick={() => setOneOpen((open) => !open)}
            >
              Run for one track…
            </button>
          </span>
        </div>

        {oneOpen ? (
          <form
            style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}
            onSubmit={(event) => {
              event.preventDefault();
              const wanted = officialId.trim();
              if (wanted) void dispatch(wanted);
            }}
          >
            <div className="field" style={{ minWidth: 0 }}>
              <label htmlFor="merge-official-id">official_id</label>
              <input
                id="merge-official-id"
                className="input"
                style={{ width: 260, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                placeholder="trial_mountain"
                value={officialId}
                autoFocus
                onChange={(event) => setOfficialId(event.currentTarget.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ fontSize: 12 }}
              disabled={running || officialId.trim() === ''}
            >
              Run for this track
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12 }}
              onClick={() => setOneOpen(false)}
            >
              Cancel
            </button>
          </form>
        ) : null}

        {runError ? (
          <ErrorNote
            error={runError}
            title="the job could not be asked to run"
            compact
            onRetry={() => void dispatch()}
          />
        ) : null}

        {notice ? (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 'var(--radius-md)',
              border: `1px solid color-mix(in srgb, ${
                notice.tone === 'ok' ? 'var(--color-ok)' : 'var(--color-warn)'
              } 45%, transparent)`,
              fontSize: 12.5,
              color: 'var(--color-neutral-300)',
              textWrap: 'pretty',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flex: 'none',
                marginTop: 6,
                background: notice.tone === 'ok' ? 'var(--color-ok)' : 'var(--color-warn)',
              }}
            />
            <span>{notice.text}</span>
          </div>
        ) : null}

        {error ? (
          <ErrorNote
            error={error}
            title="the merge requests could not be loaded"
            onRetry={() => void load()}
          />
        ) : loading && page === null ? (
          <LoadingPanel label="loading merge requests" />
        ) : rows.length === 0 ? (
          <EmptyState title="No pull requests yet">
            The job opens one per circuit whose uploads changed something. Nothing has been filed
            since this service started collecting, or the job has not run yet.
          </EmptyState>
        ) : (
          <>
            <MergeSection
              title="Waiting for a decision"
              note={
                waiting.length === 0
                  ? 'Nothing is waiting. Every pull request the job opened has been merged or closed.'
                  : 'Merge or close each one on GitHub. This page catches up the next time the job runs, which it does whenever one of its pull requests is closed, and nightly.'
              }
              rows={waiting}
              viewing={viewing}
              onView={setViewing}
            />
            <MergeSection
              title="Settled"
              note="Merged or closed on GitHub. Nothing here needs doing."
              rows={settled}
              viewing={viewing}
              onView={setViewing}
            />
          </>
        )}

        {viewed ? (
          <Card as="section" label={`What the pull request for ${viewed.track_name} would publish`}>
            <div id="merge-request-map">
              <CandidateMap officialId={viewed.official_id} refreshKey={viewed.updated_at} />
            </div>
          </Card>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 12,
          }}
        >
          <Card>
            <Kicker>first publication gate</Kicker>
            <ol style={GATE_LIST}>
              <li>
                <code>finish_crossings</code> ≥ {count(rules?.min_finish_crossings ?? 3)}, all
                within {count(rules?.crossing_spread_m ?? 5)} m of each other
              </li>
              <li>
                the contributor-confirmed layout equals the signature match, walked in driving
                order
              </li>
              <li>
                {rules && !rules.require_full_coverage
                  ? 'a complete perimeter is not required on this server'
                  : 'both sides closed, gaps = [], pct = 100'}
              </li>
            </ol>
          </Card>

          <Card>
            <Kicker>update gate</Kicker>
            <ol style={GATE_LIST}>
              <li>as above, 1 and 2</li>
              <li>coverage did not decrease on either side</li>
              <li>
                no metre changed <code>kind</code> unless MANUAL_QUORUM (
                {count(rules?.manual_quorum ?? 2)}) accounts agree
              </li>
            </ol>
          </Card>

          <Card>
            <Kicker>anything else</Kicker>
            <p className="card-body">
              Stays an open pull request for a human. The gate never rejects; it only declines to
              merge on its own.
            </p>
            {rules && !rules.auto_merge ? (
              <p className="card-body" style={{ color: 'var(--color-warn)' }}>
                Auto-merge is switched off in Settings, so every pull request waits for a human —
                gate or no gate.
              </p>
            ) : null}
          </Card>
        </div>
      </section>
    </Page>
  );
}

// ── the tables ────────────────────────────────────────────────────────────

interface MergeSectionProps {
  title: string;
  note: string;
  rows: MergeRequest[];
  viewing: string | null;
  onView: (id: string | null) => void;
}

/**
 * One of the two tables: what is waiting for somebody, and what is settled.
 *
 * Four columns, so the table fits the page: what a pull request changes goes
 * under its track's name, and the gate's criteria share one cell that wraps.
 * Nine columns that never wrap were wider than the window, and the columns
 * that pushed it out were the ones read least.
 */
function MergeSection({ title, note, rows, viewing, onView }: MergeSectionProps) {
  const headingId = `merge-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <section
      aria-labelledby={headingId}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 id={headingId} style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          {title}
          <span
            style={{
              marginLeft: 8,
              fontSize: 12.5,
              color: 'var(--color-neutral-500)',
              fontWeight: 400,
            }}
          >
            {count(rows.length)}
          </span>
        </h2>
        <span style={{ fontSize: 12.5, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
          {note}
        </span>
      </div>
      {rows.length > 0 ? (
        <Table scroll label={title} style={{ fontSize: 13 }}>
          <THead>
            <TR>
              <TH>Track</TH>
              <TH>Pull request</TH>
              <TH>Gate</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <MergeRow key={row.id} row={row} viewing={viewing === row.id} onView={onView} />
            ))}
          </TBody>
        </Table>
      ) : null}
    </section>
  );
}

function MergeRow({
  row,
  viewing,
  onView,
}: {
  row: MergeRequest;
  viewing: boolean;
  onView: (id: string | null) => void;
}) {
  const extra = extraCriteria(row.gate);
  const crossings = criterionFor(row.gate, 'crossings');
  const layout = criterionFor(row.gate, 'layout');
  const perimeter = criterionFor(row.gate, 'perimeter');
  const kinds = criterionFor(row.gate, 'kinds');
  const gate: Array<[string, string, string]> = [
    ['crossings', gateValue(crossings), gateColour(crossings)],
    ['layout', gateValue(layout, ['agree', 'disagree']), gateColour(layout)],
    ['perimeter', gateValue(perimeter), gateColour(perimeter)],
    [
      'kinds',
      gateValue(kinds, ['clear', 'pending quorum'], count(row.kind_changes)),
      gateColour(kinds),
    ],
  ];
  const status = statusOf(row);
  return (
    <TR>
      <TD title={row.official_id}>
        <span style={{ display: 'block' }}>{row.track_name}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--color-neutral-600)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{row.official_id}</span>
          {' · '}
          {row.branch.startsWith('edit/')
            ? 'track editor edit'
            : row.accounts === 1
              ? '1 account'
              : `${count(row.accounts)} accounts`}
          {row.new_metres > 0 ? ` · +${metres(row.new_metres)}` : ''}
        </span>
      </TD>
      <TD nowrap>
        {row.pr_url ? (
          <a
            href={row.pr_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, textDecoration: 'none' }}
          >
            {prNumber(row.pr_url, row.branch)}
          </a>
        ) : (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12.5,
              color: 'var(--color-neutral-600)',
            }}
          >
            {row.branch || DASH}
          </span>
        )}
        {isWaiting(row) ? (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 11.5, padding: '0 6px', marginLeft: 6 }}
            aria-expanded={viewing}
            aria-controls="merge-request-map"
            onClick={() => onView(viewing ? null : row.id)}
          >
            {viewing ? 'hide map' : 'map'}
          </button>
        ) : null}
      </TD>
      <TD title={extra || undefined}>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12 }}>
          {gate.map(([name, value, colour]) => (
            <span key={name} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              <span style={{ color: 'var(--color-neutral-500)' }}>{name}</span>{' '}
              <span style={{ color: colour, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </span>
          ))}
        </span>
      </TD>
      <TD nowrap title={status.title}>
        <Tag tone={status.tone}>{status.label}</Tag>
        <span style={{ display: 'block', fontSize: 11, color: 'var(--color-neutral-600)' }}>
          {relativeTime(row.updated_at)}
        </span>
      </TD>
    </TR>
  );
}

/**
 * What the status cell says. `auto_merged` is the table's word for "it
 * landed", so it is split by who landed it: the job on its own, or a person
 * on GitHub — which is every edit, and any survey an administrator merged.
 */
function statusOf(row: MergeRequest): { label: string; tone: TagTone; title: string } {
  if (row.status === 'auto_merged') {
    return row.merged_by === 'job'
      ? {
          label: 'auto-merged',
          tone: 'accent',
          title: 'The job merged it: the gate passed and auto-merge is on.',
        }
      : { label: 'merged', tone: 'accent', title: 'Merged on GitHub by a person.' };
  }
  if (row.status === 'closed') {
    return { label: 'closed', tone: 'quiet', title: 'Closed on GitHub without being merged.' };
  }
  return {
    label: mergeRequestLabel(row.status),
    tone: mergeRequestTone(row.status),
    title: 'Waiting for somebody to merge or close it on GitHub.',
  };
}

/** A pull request somebody still has to decide about. The job sends the
 *  geometry for exactly these, so they are the rows that can be drawn. */
function isWaiting(row: MergeRequest): boolean {
  return row.status === 'awaiting_review' || row.status === 'open' || row.status === 'ci_failed';
}

const GATE_LIST: CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 12.5,
  color: 'var(--color-neutral-300)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

/** `#233` from a pull request URL, falling back to the branch the job pushed.
 *  The number is what the admin and the repository both call it. */
function prNumber(url: string, branch: string): string {
  const match = /\/pull\/(\d+)/.exec(url);
  const found = match?.[1];
  return found ? `#${found}` : branch || url;
}
